/**
 * RASHII CURRENCY UTILITIES
 * ==========================
 * Centralized currency formatting for global readiness.
 * 
 * Standard format: AED 2,000,000
 * - No dollar signs
 * - Thousand separators required
 * - Currency prefix (not symbol)
 */

// Default currency configuration
export const CURRENCY_CONFIG = {
  defaultCurrency: 'AED',
  currencies: {
    AED: { symbol: 'AED', decimals: 0, locale: 'en-AE' },
    USD: { symbol: 'USD', decimals: 2, locale: 'en-US' },
    GBP: { symbol: 'GBP', decimals: 2, locale: 'en-GB' },
    EUR: { symbol: 'EUR', decimals: 2, locale: 'de-DE' }
  }
};

/**
 * Format amount with proper currency prefix and separators
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (default: AED)
 * @returns {string} Formatted currency string (e.g., "AED 2,000,000")
 */
export function formatCurrency(amount, currency = 'AED') {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `${currency} 0`;
  }
  
  const config = CURRENCY_CONFIG.currencies[currency] || CURRENCY_CONFIG.currencies.AED;
  
  // Format with thousand separators
  const formatted = new Intl.NumberFormat(config.locale, {
    minimumFractionDigits: config.decimals,
    maximumFractionDigits: config.decimals
  }).format(amount);
  
  return `${config.symbol} ${formatted}`;
}

/**
 * Format amount as AED (shorthand)
 */
export function formatAED(amount) {
  return formatCurrency(amount, 'AED');
}

/**
 * Parse currency string back to number
 * @param {string} value - Currency string (e.g., "AED 2,000,000")
 * @returns {number} Numeric value
 */
export function parseCurrency(value) {
  if (!value) return 0;
  // Remove currency prefix and thousand separators
  const cleaned = String(value).replace(/[^0-9.-]/g, '');
  return parseFloat(cleaned) || 0;
}

/**
 * Format as compact currency (e.g., "AED 2M")
 */
export function formatCompactCurrency(amount, currency = 'AED') {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `${currency} 0`;
  }
  
  const config = CURRENCY_CONFIG.currencies[currency] || CURRENCY_CONFIG.currencies.AED;
  
  if (amount >= 1000000000) {
    return `${config.symbol} ${(amount / 1000000000).toFixed(1)}B`;
  }
  if (amount >= 1000000) {
    return `${config.symbol} ${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `${config.symbol} ${(amount / 1000).toFixed(0)}K`;
  }
  
  return formatCurrency(amount, currency);
}

/**
 * Calculate commission breakdown
 * Matches backend rashii_platform_config.py calculations
 */
export function calculateCommission(salePrice, options = {}) {
  const {
    commissionRate = 0.02, // 2%
    vatRate = 0.05, // 5% of commission
    platformShareRate = 0.20, // 20% of commission
    arsMultiplier = 1.0,
    minCommission = 5000
  } = options;
  
  // Base commission
  let commission = salePrice * commissionRate;
  commission = Math.max(commission, minCommission);
  
  // VAT (5% of commission, NOT sale price)
  const vat = commission * vatRate;
  
  // Apply ARS multiplier
  const adjustedCommission = commission * arsMultiplier;
  const arsAdjustment = adjustedCommission - commission;
  
  // Platform share (internal)
  const platformShare = adjustedCommission * platformShareRate;
  
  // Agent net
  const agentNet = adjustedCommission - platformShare;
  
  // Total due (commission + VAT)
  const totalDue = adjustedCommission + vat;
  
  return {
    salePrice,
    commissionRate,
    commissionRateDisplay: `${(commissionRate * 100).toFixed(1)}%`,
    baseCommission: commission,
    grossCommission: adjustedCommission,
    vatRate,
    vatAmount: vat,
    platformShareRate,
    platformShare,
    agentNet,
    totalDue,
    arsMultiplier,
    arsAdjustment,
    // Formatted versions
    formatted: {
      salePrice: formatAED(salePrice),
      baseCommission: formatAED(commission),
      grossCommission: formatAED(adjustedCommission),
      vatAmount: formatAED(vat),
      platformShare: formatAED(platformShare),
      agentNet: formatAED(agentNet),
      totalDue: formatAED(totalDue),
      arsAdjustment: formatAED(arsAdjustment)
    }
  };
}

export default {
  formatCurrency,
  formatAED,
  parseCurrency,
  formatCompactCurrency,
  calculateCommission,
  CURRENCY_CONFIG
};
